import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Building2 } from "lucide-react";

interface SwissAlpsHeroProps {
  title: string;
  subtitle: string;
  ctaPrimary?: {
    text: string;
    href?: string;
    onClick?: () => void;
  };
  ctaSecondary?: {
    text: string;
    href?: string;
    onClick?: () => void;
  };
  isAuthenticated?: boolean;
  userName?: string;
}

export function SwissAlpsHero({
  title,
  subtitle,
  ctaPrimary,
  ctaSecondary,
  isAuthenticated,
  userName,
}: SwissAlpsHeroProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas size
    const resizeCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
    };

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    // Get CSS custom property colors
    const getColor = (property: string) => {
      return getComputedStyle(document.documentElement)
        .getPropertyValue(property)
        .trim();
    };

    // Mountain layer configuration
    interface MountainLayer {
      points: { x: number; y: number }[];
      color: string;
      speed: number;
      offset: number;
      baseHeight: number;
      amplitude: number;
    }

    const createMountainPoints = (
      numPoints: number,
      baseHeight: number,
      amplitude: number,
      width: number
    ): { x: number; y: number }[] => {
      const points: { x: number; y: number }[] = [];
      const segmentWidth = width / (numPoints - 1);

      for (let i = 0; i < numPoints; i++) {
        const x = i * segmentWidth;
        const randomHeight = Math.random() * amplitude;
        const y = baseHeight - randomHeight;
        points.push({ x, y });
      }

      return points;
    };

    // Initialize mountain layers (far to near)
    const width = canvas.width / (window.devicePixelRatio || 1);
    const height = canvas.height / (window.devicePixelRatio || 1);

    const layers: MountainLayer[] = [
      // Far distant mountains (lightest, slowest)
      {
        points: createMountainPoints(12, height * 0.6, height * 0.15, width),
        color: "var(--muted)",
        speed: 0.05,
        offset: 0,
        baseHeight: height * 0.6,
        amplitude: height * 0.15,
      },
      // Mid-distance mountains
      {
        points: createMountainPoints(10, height * 0.65, height * 0.2, width),
        color: "var(--muted-foreground)",
        speed: 0.1,
        offset: 0,
        baseHeight: height * 0.65,
        amplitude: height * 0.2,
      },
      // Near mountains
      {
        points: createMountainPoints(8, height * 0.7, height * 0.25, width),
        color: "var(--primary)",
        speed: 0.15,
        offset: 0,
        baseHeight: height * 0.7,
        amplitude: height * 0.25,
      },
      // Closest mountains (darkest, fastest)
      {
        points: createMountainPoints(6, height * 0.8, height * 0.3, width),
        color: "var(--accent)",
        speed: 0.2,
        offset: 0,
        baseHeight: height * 0.8,
        amplitude: height * 0.3,
      },
    ];

    // Draw mountain layer
    const drawMountain = (layer: MountainLayer) => {
      ctx.fillStyle = getColor(layer.color);
      ctx.globalAlpha =
        layer === layers[0]
          ? 0.3
          : layer === layers[1]
            ? 0.5
            : layer === layers[2]
              ? 0.7
              : 0.85;

      ctx.beginPath();
      ctx.moveTo(-10, height);

      // Create smooth mountain curve
      layer.points.forEach((point, i) => {
        const x = ((point.x + layer.offset) % (width + 100)) - 50;
        const y = point.y;

        if (i === 0) {
          ctx.lineTo(x, y);
        } else {
          const prevPoint = layer.points[i - 1];
          const prevX = ((prevPoint.x + layer.offset) % (width + 100)) - 50;
          const prevY = prevPoint.y;

          const cpX = (prevX + x) / 2;
          const cpY = (prevY + y) / 2;

          ctx.quadraticCurveTo(prevX, prevY, cpX, cpY);
        }
      });

      // Add wraparound points for seamless loop
      const firstPoint = layer.points[0];
      const wrappedX =
        ((firstPoint.x + layer.offset + width) % (width + 100)) - 50;
      ctx.lineTo(wrappedX, firstPoint.y);
      ctx.lineTo(width + 10, height);
      ctx.lineTo(-10, height);
      ctx.closePath();
      ctx.fill();

      ctx.globalAlpha = 1;
    };

    // Add snow caps on peaks
    const drawSnowCaps = (layer: MountainLayer, index: number) => {
      if (index < 2) return; // Only snow on closer mountains

      ctx.fillStyle = getColor("--background");
      ctx.globalAlpha = 0.9;

      layer.points.forEach(point => {
        const x = ((point.x + layer.offset) % (width + 100)) - 50;
        const y = point.y;

        // Only add snow to peaks (lower y values)
        if (y < layer.baseHeight - layer.amplitude * 0.7) {
          const snowHeight = (layer.baseHeight - y) * 0.3;

          ctx.beginPath();
          ctx.moveTo(x - 15, y + snowHeight);
          ctx.lineTo(x, y);
          ctx.lineTo(x + 15, y + snowHeight);
          ctx.closePath();
          ctx.fill();
        }
      });

      ctx.globalAlpha = 1;
    };

    // Sky gradient background
    const drawSky = () => {
      const gradient = ctx.createLinearGradient(0, 0, 0, height);

      // Get theme colors for sky
      const primaryColor = getColor("--primary");
      const backgroundColor = getColor("--background");

      gradient.addColorStop(0, primaryColor || "oklch(0.546 0.245 262.88)");
      gradient.addColorStop(0.4, backgroundColor || "oklch(1.0 0 0)");
      gradient.addColorStop(
        1,
        getColor("--muted") || "oklch(0.97 0.001 286.38)"
      );

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
    };

    // Animation loop
    let animationId: number;
    const animate = () => {
      // Clear and draw sky
      drawSky();

      // Update and draw each layer
      layers.forEach((layer, index) => {
        layer.offset += layer.speed;

        // Reset offset for seamless loop
        if (layer.offset > width + 100) {
          layer.offset = 0;
        }

        drawMountain(layer);
        drawSnowCaps(layer, index);
      });

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <section className="relative min-h-[600px] md:min-h-[700px] flex items-center justify-center overflow-hidden">
      {/* Animated Swiss Alps Background */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ display: "block" }}
      />

      {/* Content Overlay */}
      <div className="relative z-10 container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          {/* Welcome Message for Authenticated Users */}
          {isAuthenticated && userName && (
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background/80 backdrop-blur-sm border border-primary/20">
              <Building2 className="h-4 w-4 text-primary" />
              <span className="text-sm text-muted-foreground">
                Welcome back,{" "}
                <span className="font-semibold text-foreground">
                  {userName}
                </span>
              </span>
            </div>
          )}

          {/* Main Title */}
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-foreground drop-shadow-lg">
            <span className="bg-background/90 backdrop-blur-sm px-6 py-3 rounded-lg inline-block">
              {title}
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-lg md:text-xl lg:text-2xl text-foreground/90 max-w-3xl mx-auto">
            <span className="bg-background/80 backdrop-blur-sm px-6 py-3 rounded-lg inline-block">
              {subtitle}
            </span>
          </p>

          {/* CTA Buttons */}
          {(ctaPrimary || ctaSecondary) && (
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              {ctaPrimary && (
                <>
                  {ctaPrimary.href ? (
                    <a href={ctaPrimary.href}>
                      <Button
                        size="lg"
                        className="shadow-lg hover:shadow-xl transition-shadow"
                      >
                        {ctaPrimary.text}
                      </Button>
                    </a>
                  ) : (
                    <Button
                      size="lg"
                      onClick={ctaPrimary.onClick}
                      className="shadow-lg hover:shadow-xl transition-shadow"
                    >
                      {ctaPrimary.text}
                    </Button>
                  )}
                </>
              )}

              {ctaSecondary && (
                <>
                  {ctaSecondary.href ? (
                    <a href={ctaSecondary.href}>
                      <Button
                        size="lg"
                        variant="outline"
                        className="bg-background/80 backdrop-blur-sm hover:bg-background/90 shadow-lg hover:shadow-xl transition-all"
                      >
                        {ctaSecondary.text}
                      </Button>
                    </a>
                  ) : (
                    <Button
                      size="lg"
                      variant="outline"
                      onClick={ctaSecondary.onClick}
                      className="bg-background/80 backdrop-blur-sm hover:bg-background/90 shadow-lg hover:shadow-xl transition-all"
                    >
                      {ctaSecondary.text}
                    </Button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Bottom Fade to Content */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent pointer-events-none" />
    </section>
  );
}
